<!--
 * @Descripttion: Skill 技能列表 - 展示可用的 skill 包，点击「发送 Skill」把 skill 文件下载下来并粘贴到输入框
 *               与发送图片链路一致：主进程下载 skill 文件 -> 以「文件」形式写入剪贴板 -> Cmd+V 粘贴到目标应用
 *               skill 有两种形态，都要支持：.skill（zip 包，内含 <name>/SKILL.md + references 等资源）
 *               和 .md（扁平单文件 skill，frontmatter + 正文，适用于没有附带资源的技能）
 *               发送时把完整 fileName（含 .md/.skill 后缀）交给主进程，由主进程按原名落盘，
 *               保证 .md 技能包粘到目标应用后仍是 .md（老客户端只认 name，仍兼容）
 *               上传弹窗的默认名称取技能包文件名（去后缀），描述从包内 frontmatter 预填
 *               列表/添加/删除接口：/group/web/user/skill/queryList.do、add.do、del.do（参数走 encryptData）
 *               上传流程与话术/备忘附件一致：getUpLoadKey.do 取凭证(type=6, media) -> 七牛上传 -> add.do 落库
 * @version:
 * @Author: geory
 * @Date: 2026-10-09 16:40:00
-->
<template>
    <div class="skillWrap">
        <div class="skillHeader">
            <div class="skillTitle">Skill 技能<span class="skillCount">({{list.length}})</span></div>
            <!-- 上传 skill：选择本地技能包，填写名称/描述后上传 -->
            <div class="skillUploadBtn" :class="{'skillUploadBtnDisabled': uploading || saving}"
                @click="chooseSkillFile">
                {{ (uploading || saving) ? '上传中…' : '上传skill' }}
            </div>
            <input v-show="false" ref="skillFileInput" type="file" :accept="acceptTypes"
                @change="onSkillFileChange" />
        </div>
        <div class="skillScroll" :style="{'height': scrollHeight + 'px'}" @scroll="onScroll">
            <div v-if="!list.length" class="skillEmpty">
                {{ loading ? '加载中…' : '暂无可用技能' }}
            </div>
            <div v-for="(item, idx) in list" :key="item.id || idx" class="skillItem">
                <div class="skillItemMain">
                    <div class="skillItemHead">
                        <span class="skillItemTitle" :title="item.name">{{item.name}}</span>
                        <span class="skillItemName" :title="item.fileName">{{item.fileName}}</span>
                    </div>
                    <div v-if="item.descInfo" class="skillItemDesc" :title="item.descInfo">{{item.descInfo}}</div>
                </div>
                <div class="skillItemDel" :class="{'skillItemDelDisabled': String(delId) === String(item.id)}"
                    @click.stop="delSkill(item)">
                    {{ String(delId) === String(item.id) ? '删除中…' : '删除' }}
                </div>
                <div class="skillItemBtn" :class="{'skillItemBtnDisabled': String(sendingId) === String(item.id)}"
                    title="点击发送后会自动粘贴到刚才的输入框" @click.stop="sendSkill(item)">
                    {{ String(sendingId) === String(item.id) ? '发送中…' : '发送 Skill' }}
                </div>
            </div>
            <div v-if="list.length" class="skillMoreTip">{{ loading ? '加载中…' : (hasMore ? '下拉加载更多' : '没有更多了') }}</div>
        </div>

        <!-- 上传 skill：add.do 需要 name/descInfo，选完文件后补填 -->
        <el-dialog title="上传 Skill" :visible.sync="dialogShow" width="420px" :append-to-body="true"
            :close-on-click-modal="false" custom-class="skillUploadDialog" @close="resetUpload">
            <div class="skillForm">
                <div class="skillFormRow">
                    <div class="skillFormLabel">名称</div>
                    <el-input v-model="form.name" maxlength="30" placeholder="请输入技能名称"></el-input>
                </div>
                <div class="skillFormRow">
                    <div class="skillFormLabel">描述</div>
                    <el-input v-model="form.descInfo" type="textarea" :rows="4" maxlength="500"
                        placeholder="请输入技能描述"></el-input>
                </div>
                <div class="skillFormFile" :title="form.fileName">技能包：{{form.fileName}}</div>
                <div v-if="readingMeta" class="skillFormTip">正在读取技能包信息…</div>
                <div v-else-if="metaTip" class="skillFormTip">名称取技能包文件名，描述已从技能包中读取，可修改后上传</div>
            </div>
            <div slot="footer">
                <el-button @click="dialogShow = false">取 消</el-button>
                <el-button type="primary" :loading="saving" :disabled="readingMeta" @click="confirmUpload">确 定</el-button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
    import Vue from 'vue'
    import {
        Dialog,
        Button,
        Input
    } from 'element-ui'
    Vue.use(Dialog)
    Vue.use(Button)
    Vue.use(Input)

    import qiniuService from '@/service/qiniuService'

    // 服务端约定的上传业务类型：skill 技能包固定为 6，走 media 通道（见接口备注）
    const SKILL_UPLOAD_FUNCTION_TYPE = 6

    // 允许的技能包后缀：.skill = 目录包（zip，内含 <name>/SKILL.md 及资源）；.md = 扁平单文件 skill
    const SKILL_EXTS = ['.skill', '.md']

    // 去掉技能包后缀，得到不含后缀的技能名（落盘/发送时统一用这个名字）
    function stripSkillExt(name) {
        let out = (name || '').trim()
        SKILL_EXTS.forEach((ext) => {
            if (out.toLowerCase().endsWith(ext)) {
                out = out.slice(0, out.length - ext.length)
            }
        })
        return out
    }

    /* === skill-meta start === */
    // 从技能包（.md 单文件 / .skill zip 包）里读出 frontmatter，作为「上传」弹窗的默认名称和描述。
    // 只做尽力而为的解析：读不出来就退回文件名，不阻断上传。

    // 解析 SKILL.md 的 YAML frontmatter，只取 name / description / whenToUse
    function parseSkillFrontmatter(text) {
        const out = {}
        if (!text) return out
        const lines = text.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n').split('\n')
        // 规范要求第一行恰好是 ---，并且必须有闭合的 ---
        if (lines[0].trim() !== '---') return out
        let end = -1
        for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim() === '---') {
                end = i
                break
            }
        }
        if (end < 0) return out
        const body = lines.slice(1, end)
        for (let i = 0; i < body.length; i++) {
            const matched = /^([A-Za-z][A-Za-z0-9_-]*)\s*:\s*(.*)$/.exec(body[i])
            if (!matched) continue
            const key = matched[1]
            let value = matched[2].trim()
            if (/^[>|][-+]?$/.test(value)) {
                // 块标量（description: >- 这种）：把后续缩进行拼成一行
                const parts = []
                for (let j = i + 1; j < body.length; j++) {
                    if (body[j].trim() === '') continue
                    if (!/^\s/.test(body[j])) break
                    parts.push(body[j].trim())
                    i = j
                }
                value = parts.join(' ')
            } else {
                value = value.replace(/^["']|["']$/g, '')
                // YAML 纯量也允许折行续写（后续缩进行并入同一行）
                if (value) {
                    const parts = [value]
                    for (let j = i + 1; j < body.length; j++) {
                        if (body[j].trim() === '' || !/^\s/.test(body[j])) break
                        parts.push(body[j].trim())
                        i = j
                    }
                    value = parts.join(' ')
                }
            }
            if (value) out[key] = value
        }
        return out
    }

    // 解 zip 里的 SKILL.md：兼容 store(0) / deflate(8)，优先取层级最浅的 SKILL.md
    async function readSkillMarkdownFromZip(bytes) {
        if (!bytes || bytes.length < 22) return ''
        const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
        // 从尾部回扫 EOCD（中央目录结束记录）
        const minIdx = Math.max(0, bytes.length - 22 - 0xffff)
        let eocd = -1
        for (let i = bytes.length - 22; i >= minIdx; i--) {
            if (view.getUint32(i, true) === 0x06054b50) {
                eocd = i
                break
            }
        }
        if (eocd < 0) return ''
        const total = view.getUint16(eocd + 10, true)
        let pointer = view.getUint32(eocd + 16, true)
        const decoder = new TextDecoder('utf-8')
        let best = null
        for (let i = 0; i < total && pointer + 46 <= bytes.length; i++) {
            if (view.getUint32(pointer, true) !== 0x02014b50) break
            const method = view.getUint16(pointer + 10, true)
            const compSize = view.getUint32(pointer + 20, true)
            const nameLen = view.getUint16(pointer + 28, true)
            const extraLen = view.getUint16(pointer + 30, true)
            const commentLen = view.getUint16(pointer + 32, true)
            const localOffset = view.getUint32(pointer + 42, true)
            const entryName = decoder.decode(bytes.subarray(pointer + 46, pointer + 46 + nameLen))
            pointer += 46 + nameLen + extraLen + commentLen
            const isSkillMd = /(^|\/)SKILL\.md$/i.test(entryName)
            if (!isSkillMd && !/\.md$/i.test(entryName)) continue
            const depth = entryName.split('/').length
            const better = !best || isSkillMd > best.isSkillMd ||
                (isSkillMd === best.isSkillMd && depth < best.depth)
            if (better) {
                best = {
                    isSkillMd: isSkillMd ? 1 : 0,
                    depth: depth,
                    method: method,
                    compSize: compSize,
                    localOffset: localOffset
                }
            }
        }
        if (!best || best.localOffset + 30 > bytes.length) return ''
        if (view.getUint32(best.localOffset, true) !== 0x04034b50) return ''
        // 数据区起点：本地文件头(30) + 文件名 + 扩展区
        const start = best.localOffset + 30 +
            view.getUint16(best.localOffset + 26, true) +
            view.getUint16(best.localOffset + 28, true)
        const raw = bytes.subarray(start, start + best.compSize)
        if (best.method === 0) return decoder.decode(raw)
        if (best.method === 8) return await inflateRaw(raw)
        return ''
    }

    // 解压原始 deflate 数据；环境不支持 deflate-raw 时返回空串，不影响上传
    async function inflateRaw(bytes) {
        if (typeof DecompressionStream === 'undefined') return ''
        try {
            const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'))
            return await new Response(stream).text()
        } catch (e) {
            console.log('skill zip inflate error:', e)
            return ''
        }
    }

    function readFileText(file) {
        return new Promise((resolve) => {
            const reader = new FileReader()
            reader.onload = () => resolve(String(reader.result || ''))
            reader.onerror = () => resolve('')
            reader.readAsText(file, 'utf-8')
        })
    }

    function readFileBuffer(file) {
        return new Promise((resolve) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result || new ArrayBuffer(0))
            reader.onerror = () => resolve(new ArrayBuffer(0))
            reader.readAsArrayBuffer(file)
        })
    }

    // 按后缀读取技能包里的说明：.md 直接读文本，.skill 解压后读 SKILL.md
    async function readSkillMetaFromFile(file) {
        const fileName = ((file && file.name) || '').toLowerCase()
        let text = ''
        if (fileName.endsWith('.md')) {
            text = await readFileText(file)
        } else {
            const buffer = await readFileBuffer(file)
            text = await readSkillMarkdownFromZip(new Uint8Array(buffer))
        }
        return parseSkillFrontmatter(text)
    }
    /* === skill-meta end === */

    export default {
        name: 'SkillList',
        props: {
            height: {
                type: Number,
                default: 400
            }
        },
        data() {
            return {
                // 正在发送的技能 id
                sendingId: '',
                // 正在删除的技能 id
                delId: '',
                // 上传中标记：上传期间按钮置灰，避免重复选择文件
                uploading: false,
                // 上传弹窗：add.do 需要 name/descInfo
                dialogShow: false,
                saving: false,
                // 正在从技能包里读 frontmatter（期间禁用「确定」）
                readingMeta: false,
                // 名称/描述是否来自技能包内部（用于提示文案）
                metaTip: false,
                pendingFile: null,
                form: {
                    name: '',
                    descInfo: '',
                    fileName: ''
                },
                // 允许上传的技能包后缀（.skill 打包文件 / .md 单文件 skill）
                acceptExts: SKILL_EXTS.slice(),
                // 客户端体积上限：20M
                maxSize: 20 * 1024 * 1024,
                // 接口返回的原始列表：[{name, fileName, descInfo, id, url}]
                list: [],
                // 分页：接口 encryptData 需要 page/pageSize
                page: 0,
                pageSize: 20,
                hasMore: false,
                loading: false
            }
        },
        computed: {
            acceptTypes() {
                // input accept 属性，如 ".skill"
                return this.acceptExts.join(',')
            },
            scrollHeight() {
                // 预留头部高度（约 60px），保证滚动区占满剩余空间
                return Math.max(120, (this.height || 400) - 60)
            }
        },
        mounted() {
            this.fetchList(true)
        },
        methods: {
            // 查询技能列表：reset=true 从第一页重新拉取，否则追加下一页
            async fetchList(reset) {
                if (this.loading) return
                const page = reset ? 1 : this.page + 1
                this.loading = true
                try {
                    const res = await this.$http.skillQueryList({
                        page: page,
                        pageSize: this.pageSize
                    })
                    if (res && res.code == 200) {
                        const array = (res.data && res.data.array) || []
                        const list = array.map((val) => this.normalizeSkill(val))
                        this.list = reset ? list : this.list.concat(list)
                        this.page = page
                        // 返回条数不足一页，说明没有下一页了
                        this.hasMore = array.length >= this.pageSize
                        if (array.length) {
                            // 排查删除时「id错误！」用：这里是服务端返回的原始一条，比 normalizeSkill 后的字段更全
                            console.log("[skill-debug] queryList 首条原始数据:", array[0])
                        }
                    }
                } catch (e) {
                    console.log("skillQueryList error:", e)
                }
                this.loading = false
            },
            // 从文件地址里取技能包后缀：部分历史数据 fileName 没带后缀，用 url 兜底，
            // 避免把 .md 的单文件技能误判成 .skill 打包文件
            extFromUrl(url) {
                try {
                    const pathname = new URL(url, window.location.href).pathname
                    const last = decodeURIComponent((pathname.split('/').pop() || '').split(/[?#]/)[0])
                    const matched = /(\.[a-zA-Z0-9]+)$/.exec(last)
                    const ext = matched ? matched[1].toLowerCase() : ''
                    return SKILL_EXTS.indexOf(ext) >= 0 ? ext : ''
                } catch (e) {
                    return ''
                }
            },
            // 接口字段 -> 展示字段：name 名称 / fileName 文件名 / descInfo 描述 / url 文件地址
            // fileName 可能是 .skill 也可能是 .md，一律以服务端返回的后缀为准，不强改
            normalizeSkill(val) {
                val = val || {}
                const rawFileName = (val.fileName || '').trim()
                const name = val.name || stripSkillExt(rawFileName) || 'skill'
                let fileName = rawFileName
                if (!fileName) {
                    // 服务端没给文件名：优先按文件地址的后缀补，取不到才按打包格式兜底
                    fileName = name + (this.extFromUrl(val.url) || '.skill')
                } else if (!/\.[a-zA-Z0-9]+$/.test(fileName)) {
                    // 给了名字但没带后缀：同上
                    fileName = fileName + (this.extFromUrl(val.url) || '.skill')
                }
                return {
                    id: val.id,
                    name: name,
                    fileName: fileName,
                    descInfo: val.descInfo || '',
                    url: val.url || '',
                    // 原始一条：删除失败时打日志用，避免排查时看不到服务端真实字段
                    raw: val
                }
            },
            // 滚动到底部自动加载下一页
            onScroll(e) {
                const el = e && e.target
                if (!el || !this.hasMore || this.loading) return
                if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
                    this.fetchList(false)
                }
            },
            // 点击「上传skill」：唤起本地文件选择
            chooseSkillFile() {
                if (this.uploading || this.saving) return
                const input = this.$refs.skillFileInput
                if (!input) return
                // 重置 value，保证连续选择同一个文件也能触发 change
                input.value = ''
                input.click()
            },
            async onSkillFileChange(e) {
                const input = e && e.target
                const file = input && input.files && input.files[0]
                if (input) input.value = ''
                if (!file) return
                const errMsg = this.checkSkillFile(file)
                if (errMsg) {
                    this.$message({
                        message: errMsg,
                        type: 'warning'
                    })
                    return
                }
                // 默认名称固定取技能包文件名（去后缀），再读技能包里的 frontmatter 补全描述
                this.pendingFile = file
                this.metaTip = false
                this.form = {
                    name: stripSkillExt(file.name) || file.name.replace(/\.[^.]+$/, ''),
                    descInfo: '',
                    fileName: file.name
                }
                this.dialogShow = true
                this.readingMeta = true
                try {
                    const meta = await readSkillMetaFromFile(file)
                    // 期间用户可能已取消/换了文件，避免串数据
                    if (this.pendingFile !== file) return
                    // 注意：名称不再用包内 frontmatter 的 name 覆盖，始终以技能包文件名为准
                    const desc = meta.description || meta.whenToUse || ''
                    if (desc) {
                        this.form.descInfo = desc
                        this.metaTip = true
                    }
                } catch (err) {
                    console.log("read skill meta error:", err)
                } finally {
                    if (this.pendingFile === file) {
                        this.readingMeta = false
                    }
                }
            },
            // 上传前校验：后缀 + 体积
            checkSkillFile(file) {
                const name = (file.name || '').toLowerCase()
                const valid = this.acceptExts.some(ext => name.endsWith(ext))
                if (!valid) {
                    return '请选择 ' + this.acceptExts.join('/') + ' 格式的技能包'
                }
                if (file.size > this.maxSize) {
                    return '技能包不能超过 ' + (this.maxSize / 1024 / 1024) + 'M'
                }
                return ''
            },
            resetUpload() {
                this.pendingFile = null
                this.readingMeta = false
                this.metaTip = false
                this.form = {
                    name: '',
                    descInfo: '',
                    fileName: ''
                }
            },
            // 上传本地技能包到七牛 media 通道，返回文件地址
            // 凭证接口：/group/web/qiniu/getUpLoadKey.do（functionType=6，使用 mediaToken/mediaDomain）
            async uploadSkillFile(file) {
                this.uploading = true
                const suffix = (file.name.split('.').pop() || '').toLowerCase()
                const tokenParams = {
                    functionType: SKILL_UPLOAD_FUNCTION_TYPE,
                    targetId: localStorage.getItem("edujia:group:web:userid") || ""
                }
                if (suffix) {
                    tokenParams.mediaSuffix = JSON.stringify([suffix])
                }
                console.log("skill getFileUploadToken params:", tokenParams)
                const enResp = await this.$http.getFileUploadToken(tokenParams)
                if (!enResp || enResp.code != 200) {
                    throw new Error("获取上传凭证失败")
                }
                const data = this.parseUploadToken(enResp.data)
                const object = (data && data.object) || {}
                const mediaToken = object.mediaToken || []
                if (!mediaToken.length) {
                    throw new Error("获取上传凭证失败")
                }
                const resultArr = await qiniuService.uploadImgArr(mediaToken, [{
                    id: 'skill',
                    file: file
                }])
                const key = resultArr && resultArr[0] && resultArr[0].url && resultArr[0].url.key
                if (!key) {
                    throw new Error("技能包上传失败")
                }
                return (object.mediaDomain || '') + key
            },
            // getUpLoadKey.do 返回的 data 是加密串，兼容已是对象的情况
            parseUploadToken(raw) {
                if (raw && typeof raw === 'object') return raw
                try {
                    const key = localStorage.getItem("edujia:group:web:key") || undefined
                    return JSON.parse(this.$common.decrypt(raw, key))
                } catch (e) {
                    console.log("skill upload token decrypt error:", e)
                    return {}
                }
            },
            // 确认上传：七牛上传拿到 url -> add.do 落库 -> 刷新列表
            async confirmUpload() {
                if (this.saving || this.readingMeta) return
                const name = (this.form.name || '').trim()
                if (!name) {
                    this.$message({
                        message: '请输入技能名称',
                        type: 'warning'
                    })
                    return
                }
                if (!this.pendingFile) {
                    this.dialogShow = false
                    return
                }
                this.saving = true
                try {
                    const url = await this.uploadSkillFile(this.pendingFile)
                    const res = await this.$http.skillAdd({
                        name: name,
                        fileName: this.form.fileName,
                        descInfo: (this.form.descInfo || '').trim(),
                        url: url
                    })
                    if (res && res.code == 200) {
                        this.$message({
                            message: '技能上传成功',
                            type: 'success'
                        })
                        this.dialogShow = false
                        this.fetchList(true)
                    }
                } catch (e) {
                    console.log("skill upload error:", e)
                    this.$message({
                        message: (e && e.message) || '技能上传失败，请稍后重试',
                        type: 'error'
                    })
                }
                this.saving = false
                this.uploading = false
            },
            // 删除技能：del.do（encryptData: {id} 为服务端返回的「文件id」）
            // 注意：这里不拦截任何 id 值，服务端给什么就发什么，便于和服务端对齐口径
            async delSkill(item) {
                if (!item || this.delId !== '') return
                const id = item.id
                // 只提示、不拦截：列表回 0（未回填真实文件id）时服务端会给「id错误！」
                if (id === undefined || id === null || id === '' ||
                    (isFinite(Number(id)) && Number(id) <= 0)) {
                    console.warn("skillDel: id 看起来不是有效的文件id，仍按原样发送（" +
                        JSON.stringify(id) + "）", item.raw || item)
                }
                try {
                    await this.$confirm('确定删除该 Skill？删除后无法恢复', '提示', {
                        confirmButtonText: '确定',
                        cancelButtonText: '取消',
                        type: 'warning'
                    })
                } catch (e) {
                    return
                }
                this.delId = id
                try {
                    // 实际提交给 del.do 的只有 id（encryptData: {id}）；下面另一条日志只是列表项上下文，不参与提交
                    const params = {
                        id: id
                    }
                    console.log("skillDel 提交参数:", params)
                    console.log("skillDel 列表项上下文:", {
                        name: item.name,
                        fileName: item.fileName,
                        url: item.url
                    })
                    const res = await this.$http.skillDel(params)
                    if (res && res.code == 200) {
                        const idx = this.list.findIndex((v) => String(v.id) === String(id))
                        if (idx >= 0) {
                            this.list.splice(idx, 1)
                        }
                        this.$message({
                            message: '删除成功',
                            type: 'success'
                        })
                    } else if (res && res.message) {
                        // 服务端拒绝时把「删的是哪一条」一起打出来，便于定位 id 语义
                        console.warn("skillDel failed:", res.message, "id =", id, "item =", item)
                    }
                } catch (e) {
                    console.log("skillDel error:", e)
                }
                this.delId = ''
            },
            isElectron() {
                return !!(this.$common && this.$common.isElectron && this.$common.isElectron())
            },
            ipc() {
                if (!this.isElectron()) return null
                try {
                    return window.require('electron').ipcRenderer
                } catch (_) {
                    return null
                }
            },
            // 发送 skill：Electron 走主进程（下载 + 写剪贴板 + 粘贴），网页端降级为直接下载文件
            async sendSkill(item) {
                if (!item || !item.url || this.sendingId !== '') return
                const r = this.ipc()
                if (!r) {
                    this.downloadInBrowser(item)
                    return
                }
                this.sendingId = item.id
                try {
                    const res = await r.invoke('send-skill', {
                        url: item.url,
                        // 完整文件名（含 .md/.skill 后缀）：主进程按它落盘，保证 .md 技能包不会被改成 .skill
                        fileName: item.fileName,
                        // name 只作为老版本客户端的兜底（老客户端会自己补 .skill 后缀）
                        name: stripSkillExt(item.fileName || item.name) || 'skill'
                    })
                    if (res && res.ok) {
                        this.$message({
                            message: '已发送 ' + res.fileName,
                            type: 'success'
                        })
                    } else {
                        this.$message({
                            message: (res && res.message) || '发送失败，请重试',
                            type: 'warning'
                        })
                    }
                } catch (e) {
                    this.$message({
                        message: (e && e.message) || '发送失败，请重试',
                        type: 'warning'
                    })
                } finally {
                    this.sendingId = ''
                }
            },
            // 网页端无法操作剪贴板文件，降级为浏览器下载
            downloadInBrowser(item) {
                const a = document.createElement('a')
                a.href = item.url
                a.download = item.fileName || ((item.name || 'skill') + '.skill')
                a.target = '_blank'
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                this.$message({
                    message: '已开始下载 skill 文件',
                    type: 'success'
                })
            }
        }
    }
</script>

<style scoped>
    .skillWrap {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
        padding: 8px 12px;
        background: #fff;
    }

    .skillHeader {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 44px;
    }

    .skillTitle {
        font-size: 15px;
        font-weight: 600;
        color: #333;
    }

    .skillCount {
        margin-left: 6px;
        font-size: 13px;
        font-weight: 400;
        color: #999;
    }

    .skillUploadBtn {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        height: 26px;
        padding: 0 12px;
        font-size: 13px;
        color: #FB5D5C;
        background: #fff;
        border: 1px solid #FB5D5C;
        border-radius: 13px;
        cursor: pointer;
        user-select: none;
        transition: background 0.15s;
    }

    .skillUploadBtn:hover {
        background: #FFF1F1;
    }

    .skillUploadBtnDisabled,
    .skillUploadBtnDisabled:hover {
        color: #f5b7b6;
        background: #fff;
        border-color: #f5b7b6;
        cursor: default;
    }

    .skillScroll {
        flex: 1;
        overflow-y: auto;
        min-height: 0;
    }

    .skillEmpty {
        margin-top: 60px;
        text-align: center;
        font-size: 13px;
        color: #bbb;
    }

    .skillMoreTip {
        padding: 6px 0 2px;
        text-align: center;
        font-size: 12px;
        color: #bbb;
    }

    .skillItem {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 12px;
        margin-bottom: 8px;
        border: 1px solid #eee;
        border-radius: 6px;
        transition: background 0.15s;
    }

    .skillItem:hover {
        background: #f7f8fa;
        border-color: #e4e7ed;
    }

    .skillItemMain {
        flex: 1;
        min-width: 0;
        margin-right: 12px;
    }

    .skillItemHead {
        display: flex;
        align-items: center;
        min-width: 0;
    }

    .skillItemTitle {
        font-size: 14px;
        color: #333;
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .skillItemName {
        flex-shrink: 0;
        max-width: 60%;
        margin-left: 8px;
        padding: 1px 6px;
        font-size: 12px;
        color: #409eff;
        background: #ecf5ff;
        border-radius: 3px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .skillItemDesc {
        margin-top: 4px;
        font-size: 12px;
        color: #999;
        line-height: 1.5;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .skillItemDel {
        flex-shrink: 0;
        margin-right: 8px;
        padding: 6px 12px;
        font-size: 13px;
        color: #909399;
        background: #fff;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        cursor: pointer;
    }

    .skillItemDel:hover {
        color: #FB5D5C;
        border-color: #f5b7b6;
        background: #FFF7F7;
    }

    .skillItemDelDisabled,
    .skillItemDelDisabled:hover {
        color: #c8c9cc;
        border-color: #ebeef5;
        background: #fff;
        cursor: default;
    }

    .skillItemBtn {
        flex-shrink: 0;
        padding: 6px 14px;
        font-size: 13px;
        color: #fff;
        background: #FB5D5C;
        border-radius: 4px;
        cursor: pointer;
    }

    .skillItemBtn:hover {
        background: #fc7776;
    }

    .skillItemBtnDisabled {
        background: #f5b7b6;
        cursor: default;
    }

    .skillFormRow {
        display: flex;
        align-items: flex-start;
        margin-bottom: 14px;
    }

    .skillFormLabel {
        flex-shrink: 0;
        width: 52px;
        padding-top: 8px;
        font-size: 13px;
        color: #606266;
    }

    .skillFormFile {
        font-size: 12px;
        color: #909399;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .skillFormTip {
        margin-top: 8px;
        font-size: 12px;
        color: #909399;
    }
</style>
